import { useCallback } from 'react'
import { config } from '@config/config'
import { useLazyGetCustomerContextQuery, useLazyGetSessionQuery } from '@redux/queries/sessions'
import { createSessionExpiredFetchError } from '@utils/errorUtil'
import { isCustomerNumberValid } from '@utils/isCustomerNumberValid'
import { useEnvironment } from './useEnvironment'

type UseValidateSessionReturn = {
  checkSessionValidOrThrow: () => Promise<void>
  checkSessionValid: () => Promise<boolean>
}

export const useValidateSession = (): UseValidateSessionReturn => {
  const { anonymousUserId } = useEnvironment()

  const [fetchSession] = useLazyGetSessionQuery()
  const [fetchCustomerContext] = useLazyGetCustomerContextQuery()

  const checkSessionValidOrThrow = useCallback(async (): Promise<void> => {
    const disableSessionIdCheck = config.getFeatures()?.disableAuthTokenSessionIdCheck

    const customerContext = await fetchCustomerContext().unwrap()
    if (!customerContext?.sessionId && !disableSessionIdCheck) {
      throw createSessionExpiredFetchError('Got empty session id from commerce - session expired?')
    }

    const session = await fetchSession(customerContext?.sessionId).unwrap()
    if (!session?.customer?.id || !isCustomerNumberValid(session?.customer?.id, anonymousUserId)) {
      throw createSessionExpiredFetchError('Got invalid customer number from commerce - session expired?')
    }
  }, [anonymousUserId, fetchSession, fetchCustomerContext])

  const checkSessionValid = useCallback(async (): Promise<boolean> => {
    const disableSessionIdCheck = config.getFeatures()?.disableAuthTokenSessionIdCheck

    const customerContext = await fetchCustomerContext().unwrap()
    if (!customerContext?.sessionId && !disableSessionIdCheck) {
      return false
    }

    const session = await fetchSession(customerContext?.sessionId).unwrap()
    if (!session?.customer?.id || !isCustomerNumberValid(session?.customer?.id, anonymousUserId)) {
      return false
    }

    return true
  }, [anonymousUserId, fetchSession, fetchCustomerContext])

  return {
    checkSessionValidOrThrow,
    checkSessionValid,
  }
}
